/**
 * CourseService
 * Service for Course entity operations
 * Route: api/courses
 */
import { BaseService } from '../base/BaseService';
/**
 * CourseService
 * Handles course CRUD operations and ModelView endpoints
 */
export class CourseService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CourseService.DEFAULT_SERVICE_PATH,
            basePath: CourseService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ============================================================
    // Entity CRUD Operations
    // ============================================================
    /**
     * Get all courses
     * @returns Promise resolving to array of courses
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all courses (raw response)
     * @returns Promise resolving to ResponseData with courses
     */
    getAllRaw() {
        const url = this.buildUrl('');
        return this.http.get(url);
    }
    /**
     * Get course by ID
     * @param id - Course ID
     * @returns Promise resolving to course or null
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course by ID (raw response)
     * @param id - Course ID
     * @returns Promise resolving to ResponseData with course
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get courses by category ID
     * @param categoryId - Category ID
     * @returns Promise resolving to array of courses
     */
    async getByCategory(categoryId) {
        const response = await this.getByCategoryRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get courses by category ID (raw response)
     * @param categoryId - Category ID
     * @returns Promise resolving to ResponseData with courses
     */
    getByCategoryRaw(categoryId) {
        const endpoint = this.getEndpoint('getByCategory', `category/${categoryId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get course sessions
     * @param id - Course ID
     * @returns Promise resolving to array of course sessions
     */
    async getSessions(id) {
        const response = await this.getSessionsRaw(id);
        return response.data ?? [];
    }
    /**
     * Get course sessions (raw response)
     * @param id - Course ID
     * @returns Promise resolving to ResponseData with course sessions
     */
    getSessionsRaw(id) {
        const endpoint = this.getEndpoint('getSessions', `${id}/sessions`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new course
     * @param course - Course data
     * @returns Promise resolving to created course
     */
    async create(course) {
        const response = await this.createRaw(course);
        return response.data ?? null;
    }
    /**
     * Create a new course (raw response)
     * @param course - Course data
     * @returns Promise resolving to ResponseData with created course
     */
    createRaw(course) {
        const url = this.buildUrl('');
        return this.http.post(url, course);
    }
    /**
     * Update an existing course
     * @param id - Course ID
     * @param course - Updated course data
     * @returns Promise resolving to updated course
     */
    async update(id, course) {
        const response = await this.updateRaw(id, course);
        return response.data ?? null;
    }
    /**
     * Update an existing course (raw response)
     * @param id - Course ID
     * @param course - Updated course data
     * @returns Promise resolving to ResponseData with updated course
     */
    updateRaw(id, course) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, course);
    }
    /**
     * Publish a course
     * @param id - Course ID
     * @returns Promise resolving to published course
     */
    async publish(id) {
        const response = await this.publishRaw(id);
        return response.data ?? null;
    }
    /**
     * Publish a course (raw response)
     * @param id - Course ID
     * @returns Promise resolving to ResponseData with published course
     */
    publishRaw(id) {
        const endpoint = this.getEndpoint('publish', `${id}/publish`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, {});
    }
    /**
     * Delete a course
     * @param id - Course ID
     * @returns Promise resolving to true if successful
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a course (raw response)
     * @param id - Course ID
     * @returns Promise resolving to ResponseData
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ============================================================
    // ModelView Endpoints
    // ============================================================
    /**
     * Get course view by ID
     * @param id - Course ID
     * @returns Promise resolving to course model view or null
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course view by ID (raw response)
     * @param id - Course ID
     * @returns Promise resolving to ResponseData with course model view
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('getView', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered course list view
     * @param filter - Optional filter criteria
     * @returns Promise resolving to array of course model views
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered course list view (raw response)
     * @param filter - Optional filter criteria
     * @returns Promise resolving to ResponseData with course model views
     */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || null);
    }
    /**
     * Get course views by category
     * @param categoryId - Category ID
     * @returns Promise resolving to array of course model views
     */
    async getCategoryViews(categoryId) {
        const response = await this.getCategoryViewsRaw(categoryId);
        return response.data ?? [];
    }
    /**
     * Get course views by category (raw response)
     * @param categoryId - Category ID
     * @returns Promise resolving to ResponseData with course model views
     */
    getCategoryViewsRaw(categoryId) {
        const endpoint = this.getEndpoint('getCategoryViews', `category/${categoryId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ============================================================
    // Visibility — audience management (Restricted courses only)
    // ============================================================
    /**
     * Read the role/ministry/department audience grants for a Restricted course.
     * Empty lists == visible to all signed-in users.
     */
    async getAudience(courseId) {
        const response = await this.getAudienceRaw(courseId);
        return response.data ?? { roleNames: [], ministryIds: [], departmentIds: [] };
    }
    getAudienceRaw(courseId) {
        return this.http.get(this.buildUrl(`${courseId}/audience`));
    }
    /** Wholesale replace a course's audience grants. */
    async setAudience(courseId, audience) {
        const response = await this.setAudienceRaw(courseId, audience);
        return response.data ?? audience;
    }
    setAudienceRaw(courseId, audience) {
        return this.http.put(this.buildUrl(`${courseId}/audience`), audience);
    }
}
CourseService.DEFAULT_SERVICE_PATH = 'resources';
CourseService.BASE_PATH = 'api/courses';
//# sourceMappingURL=CourseService.js.map